/**
 * What the money screens read from hg-billing. Every key starts with
 * `MONEY_KEY`, so a change to the money refreshes all of it in one call.
 *
 * hg-billing's staff endpoints are an admin's and a superadmin's; it holds
 * no service areas, so an admin sees the whole of it. Its lists are read a
 * slice at a time, by cursor.
 */
import { infiniteQueryOptions, keepPreviousData, queryOptions } from "@tanstack/react-query";

import { billingApi, orderApi, unwrap } from "@/shared/api/clients";

import type {
  AdjustmentStatus,
  BankCallOutcome,
  PayoutOwner,
  PayoutStatus,
  ReportGroup,
  TopupProvider,
  TopupState,
  WalletOwner,
  WalletSort,
  WalletState,
} from "./types";

export const MONEY_KEY = ["money"] as const;

/** Rows of a slice. */
export const LIMIT = 25;

export const OVERVIEW_KEY = [...MONEY_KEY, "overview"] as const;

/** The money now: here for the counts of the wallets in each state, a currency at a time. */
export const overviewQuery = queryOptions({
  queryKey: OVERVIEW_KEY,
  queryFn: async ({ signal }) => {
    const { data } = await unwrap(billingApi.GET("/v2/admin/money/overview", { signal }));
    return data;
  },
  staleTime: 30_000,
});

/** The filters `GET /v2/admin/wallets` has. There are no others. */
export interface WalletsFilter {
  state?: WalletState;
  q?: string;
  currency?: string;
  /** Only the wallets of specialists approved to work, or only the others. */
  approved?: boolean;
  sort?: WalletSort;
}

export function walletsQuery(filter: WalletsFilter) {
  return infiniteQueryOptions({
    queryKey: [...MONEY_KEY, "wallets", filter] as const,
    queryFn: ({ pageParam, signal }) =>
      unwrap(
        billingApi.GET("/v2/admin/wallets", {
          params: {
            query: {
              ...filter,
              limit: LIMIT,
              ...(pageParam === null ? {} : { cursor: pageParam }),
            },
          },
          signal,
        }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) =>
      last.pagination.has_more ? (last.pagination.next_cursor ?? null) : null,
  });
}

/** What narrows the wallets besides the state: the tabs are counted with it. */
export type WalletCountsFilter = Pick<WalletsFilter, "q" | "currency" | "approved">;

/**
 * How many wallets the list's filters leave in all and in each state: the
 * tabs. A state is not a filter here: each is a count.
 */
export function walletCountsQuery(filter: WalletCountsFilter) {
  return queryOptions({
    queryKey: [...MONEY_KEY, "wallets", "counts", filter] as const,
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        billingApi.GET("/v2/admin/wallets/counts", { params: { query: filter }, signal }),
      );
      return data;
    },
    placeholderData: keepPreviousData,
  });
}

export function walletKey(owner: WalletOwner) {
  return [...MONEY_KEY, "wallet", owner.by, owner.id] as const;
}

/** One wallet, by its provider or by a specialist who works through it. */
export function walletQuery(owner: WalletOwner) {
  return queryOptions({
    queryKey: walletKey(owner),
    queryFn: async ({ signal }) => {
      const { data } =
        owner.by === "provider"
          ? await unwrap(
              billingApi.GET("/v2/admin/providers/{provider_id}/wallet", {
                params: { path: { provider_id: owner.id } },
                signal,
              }),
            )
          : await unwrap(
              billingApi.GET("/v2/admin/specialists/{specialist_id}/wallet", {
                params: { path: { specialist_id: owner.id } },
                signal,
              }),
            );
      return data;
    },
  });
}

/** What narrows a wallet's history: `GET …/wallet/entries` has no other filters. */
export interface WalletEntriesFilter {
  entry_type?: string;
  /** What happened from this moment on. */
  occurred_from?: string;
  /** What happened before this moment. */
  occurred_to?: string;
  /** Lines of at least this sum, in minor units, whichever way the money went. */
  amount_min?: number;
  amount_max?: number;
  /** A part of staff's memo or of the reason code: three characters at least. */
  q?: string;
}

/** A wallet's history, the newest first, narrowed by its filters. */
export function walletEntriesQuery(owner: WalletOwner, filter: WalletEntriesFilter) {
  return infiniteQueryOptions({
    queryKey: [...walletKey(owner), "entries", filter] as const,
    queryFn: ({ pageParam, signal }) => {
      const query = {
        ...filter,
        limit: LIMIT,
        ...(pageParam === null ? {} : { cursor: pageParam }),
      };
      return owner.by === "provider"
        ? unwrap(
            billingApi.GET("/v2/admin/providers/{provider_id}/wallet/entries", {
              params: { path: { provider_id: owner.id }, query },
              signal,
            }),
          )
        : unwrap(
            billingApi.GET("/v2/admin/specialists/{specialist_id}/wallet/entries", {
              params: { path: { specialist_id: owner.id }, query },
              signal,
            }),
          );
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) =>
      last.pagination.has_more ? (last.pagination.next_cursor ?? null) : null,
  });
}

/** The span a wallet's totals are of: open on a side that is left out. */
export interface SummarySpan {
  occurred_from?: string;
  occurred_to?: string;
}

/**
 * What a wallet took in and gave out, added up by hg-billing from the
 * ledger: of all time, or of what happened in the span.
 */
export function walletSummaryQuery(owner: WalletOwner, span: SummarySpan) {
  return queryOptions({
    queryKey: [...walletKey(owner), "summary", span] as const,
    queryFn: async ({ signal }) => {
      const { data } =
        owner.by === "provider"
          ? await unwrap(
              billingApi.GET("/v2/admin/providers/{provider_id}/wallet/summary", {
                params: { path: { provider_id: owner.id }, query: span },
                signal,
              }),
            )
          : await unwrap(
              billingApi.GET("/v2/admin/specialists/{specialist_id}/wallet/summary", {
                params: { path: { specialist_id: owner.id }, query: span },
                signal,
              }),
            );
      return data;
    },
    placeholderData: keepPreviousData,
  });
}

/** A person's saved cards, as hg-billing shows them to staff: masked, the ones taken off too. */
export function userCardsQuery(userId: string) {
  return queryOptions({
    queryKey: [...MONEY_KEY, "cards", userId] as const,
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        billingApi.GET("/v2/admin/users/{user_id}/cards", {
          params: { path: { user_id: userId } },
          signal,
        }),
      );
      return data.cards;
    },
  });
}

// ---- top-ups ----

/** The filters `GET /v2/admin/topups` has that the list uses. */
export interface TopupsFilter {
  state?: TopupState;
  provider?: TopupProvider;
  /** Only the ones a payment system later said had failed. */
  contested?: boolean;
  /** Only the card top-ups marked for staff and not dealt with yet. */
  needs_review?: boolean;
  specialist_id?: string;
  /** Begun from this moment on. */
  created_from?: string;
  /** Begun before this moment. */
  created_to?: string;
  /** Of at least this sum, as the payer paid it, in minor units. */
  amount_min?: number;
  amount_max?: number;
  /**
   * A part of what a top-up is known by: the payment system's id of it, our
   * number for it, what the payer typed as the account, the bank's id of a
   * card payment. Three characters at least; never a name.
   */
  q?: string;
}

export function topupsQuery(filter: TopupsFilter) {
  return infiniteQueryOptions({
    queryKey: [...MONEY_KEY, "topups", filter] as const,
    queryFn: ({ pageParam, signal }) =>
      unwrap(
        billingApi.GET("/v2/admin/topups", {
          params: {
            query: {
              ...filter,
              limit: LIMIT,
              ...(pageParam === null ? {} : { cursor: pageParam }),
            },
          },
          signal,
        }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) =>
      last.pagination.has_more ? (last.pagination.next_cursor ?? null) : null,
  });
}

/**
 * What narrows the top-ups besides the tab: the tabs are counted with all of
 * it (`GET /v2/admin/topups/counts` takes every filter the list has).
 */
export type TopupCountsFilter = Omit<TopupsFilter, "state" | "contested" | "needs_review">;

/**
 * How many top-ups the list's filters leave in each state, how many are
 * contested and how many card ones wait for staff: the tabs.
 */
export function topupCountsQuery(filter: TopupCountsFilter) {
  return queryOptions({
    queryKey: [...MONEY_KEY, "topups", "counts", filter] as const,
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        billingApi.GET("/v2/admin/topups/counts", { params: { query: filter }, signal }),
      );
      return data;
    },
    placeholderData: keepPreviousData,
  });
}

/** One top-up, with every call its payment system made about it, and every call to the bank about a card one. */
export function topupQuery(topupId: string) {
  return queryOptions({
    queryKey: [...MONEY_KEY, "topup", topupId] as const,
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        billingApi.GET("/v2/admin/topups/{topup_id}", {
          params: { path: { topup_id: topupId } },
          signal,
        }),
      );
      return data;
    },
  });
}

/**
 * Specialists by a part of the name, the phone or the number, from hg-order:
 * whom a top-up that waits in suspense is given to.
 */
export function specialistSearchQuery(q: string) {
  return queryOptions({
    queryKey: [...MONEY_KEY, "specialist-search", q] as const,
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        orderApi.GET("/v2/admin/specialists", {
          params: { query: { q, page: 1, per_page: 6 } },
          signal,
        }),
      );
      return data;
    },
    staleTime: 60_000,
  });
}

// ---- adjustments ----

/** The filters `GET /v2/admin/ledger/adjustments` has. */
export interface AdjustmentsFilter {
  status?: AdjustmentStatus;
  provider_id?: string;
}

/** Every list of the adjustments, whatever its filter. */
export const ADJUSTMENTS_KEY = [...MONEY_KEY, "adjustments"] as const;

export function adjustmentsQuery(filter: AdjustmentsFilter) {
  return infiniteQueryOptions({
    queryKey: [...ADJUSTMENTS_KEY, filter] as const,
    queryFn: ({ pageParam, signal }) =>
      unwrap(
        billingApi.GET("/v2/admin/ledger/adjustments", {
          params: {
            query: {
              ...filter,
              limit: LIMIT,
              ...(pageParam === null ? {} : { cursor: pageParam }),
            },
          },
          signal,
        }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) =>
      last.pagination.has_more ? (last.pagination.next_cursor ?? null) : null,
  });
}

export function adjustmentKey(adjustmentId: string) {
  return [...MONEY_KEY, "adjustment", adjustmentId] as const;
}

/** One adjustment: who asked and who decided by name, the wallet's specialist, its entry's id. */
export function adjustmentQuery(adjustmentId: string) {
  return queryOptions({
    queryKey: adjustmentKey(adjustmentId),
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        billingApi.GET("/v2/admin/ledger/adjustments/{adjustment_id}", {
          params: { path: { adjustment_id: adjustmentId } },
          signal,
        }),
      );
      return data;
    },
  });
}

// ---- payouts ----

/** The filters `GET /v2/admin/payouts` has. */
export interface PayoutsFilter {
  status?: PayoutStatus;
  market_code?: string;
  owner_type?: PayoutOwner;
  provider_id?: string;
}

/** Payouts: a queue (`requested`, `approved`) the oldest first, anything else the newest first. */
export function payoutsQuery(filter: PayoutsFilter) {
  return infiniteQueryOptions({
    queryKey: [...MONEY_KEY, "payouts", filter] as const,
    queryFn: ({ pageParam, signal }) =>
      unwrap(
        billingApi.GET("/v2/admin/payouts", {
          params: {
            query: {
              ...filter,
              limit: LIMIT,
              ...(pageParam === null ? {} : { cursor: pageParam }),
            },
          },
          signal,
        }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) =>
      last.pagination.has_more ? (last.pagination.next_cursor ?? null) : null,
  });
}

/** What narrows the payouts besides the tab: the tabs are counted with it. */
export type PayoutCountsFilter = Pick<PayoutsFilter, "market_code" | "owner_type" | "provider_id">;

/** How many payouts the list's filters leave in each status: the tabs. */
export function payoutCountsQuery(filter: PayoutCountsFilter) {
  return queryOptions({
    queryKey: [...MONEY_KEY, "payouts", "counts", filter] as const,
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        billingApi.GET("/v2/admin/payouts/counts", { params: { query: filter }, signal }),
      );
      return data;
    },
    placeholderData: keepPreviousData,
  });
}

/** One payout: whom it is for, where it is sent, who decided it and its entries' ids. */
export function payoutQuery(payoutId: string) {
  return queryOptions({
    queryKey: [...MONEY_KEY, "payout", payoutId] as const,
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        billingApi.GET("/v2/admin/payouts/{payout_id}", {
          params: { path: { payout_id: payoutId } },
          signal,
        }),
      );
      return data;
    },
  });
}

/**
 * Everything the ledger holds about one payout or adjustment, its reversals
 * included: a handful of entries, read at once.
 */
export function entriesAboutKey(type: "payout" | "adjustment", id: string) {
  return [...MONEY_KEY, "ledger", "about", type, id] as const;
}

export function entriesAboutQuery(type: "payout" | "adjustment", id: string) {
  return queryOptions({
    queryKey: entriesAboutKey(type, id),
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        billingApi.GET("/v2/admin/ledger/entries", {
          params: { query: { reference_type: type, reference_id: id, limit: LIMIT } },
          signal,
        }),
      );
      return data;
    },
  });
}

// ---- the ledger ----

/** The platform's own accounts, with what each holds: a few a currency, all at once. */
export const ledgerAccountsQuery = queryOptions({
  queryKey: [...MONEY_KEY, "ledger", "accounts"] as const,
  queryFn: async ({ signal }) => {
    const { data } = await unwrap(billingApi.GET("/v2/admin/ledger/accounts", { signal }));
    return data.accounts;
  },
});

/** Whether the books are whole: the last daily check, and what is kept without an entry. */
export const ledgerHealthQuery = queryOptions({
  queryKey: [...MONEY_KEY, "ledger", "health"] as const,
  queryFn: async ({ signal }) => {
    const { data } = await unwrap(billingApi.GET("/v2/admin/ledger/health", { signal }));
    return data;
  },
});

/** The filters of `GET /v2/admin/ledger/entries` the screen uses. */
export interface LedgerFilter {
  entry_type?: string;
  /** Entries with a posting in an account of this kind. */
  kind?: string;
  /** What happened from this moment on. */
  occurred_from?: string;
  /** What happened before this moment. */
  occurred_to?: string;
  /** Entries with a posting of at least this sum, in minor units. */
  amount_min?: number;
  amount_max?: number;
  /** A part of the memo or of the reason code: three characters at least. */
  q?: string;
}

export function ledgerEntriesQuery(filter: LedgerFilter) {
  return infiniteQueryOptions({
    queryKey: [...MONEY_KEY, "ledger", "entries", filter] as const,
    queryFn: ({ pageParam, signal }) =>
      unwrap(
        billingApi.GET("/v2/admin/ledger/entries", {
          params: {
            query: {
              ...filter,
              limit: LIMIT,
              ...(pageParam === null ? {} : { cursor: pageParam }),
            },
          },
          signal,
        }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) =>
      last.pagination.has_more ? (last.pagination.next_cursor ?? null) : null,
  });
}

// ---- the report ----

/** What `GET /v2/admin/money/report` is asked: a span of the market's days, told by day, week or month. */
export interface ReportFilter {
  from: string;
  to: string;
  group?: ReportGroup;
  market?: string;
}

/** A market's money by period, counted by hg-billing from the ledger when asked. */
export function moneyReportQuery(filter: ReportFilter) {
  return queryOptions({
    queryKey: [...MONEY_KEY, "report", filter] as const,
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        billingApi.GET("/v2/admin/money/report", { params: { query: filter }, signal }),
      );
      return data;
    },
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

// ---- the calls to the bank ----

/** The filters `GET /v2/admin/bank-calls` has. */
export interface BankCallsFilter {
  method?: string;
  outcome?: BankCallOutcome;
  topup_id?: string;
  binding_id?: string;
  called_from?: string;
  called_to?: string;
}

/** Every call made to the bank of card top-ups, the newest first: a superadmin's. */
export function bankCallsQuery(filter: BankCallsFilter) {
  return infiniteQueryOptions({
    queryKey: [...MONEY_KEY, "bank-calls", filter] as const,
    queryFn: ({ pageParam, signal }) =>
      unwrap(
        billingApi.GET("/v2/admin/bank-calls", {
          params: {
            query: {
              ...filter,
              limit: LIMIT,
              ...(pageParam === null ? {} : { cursor: pageParam }),
            },
          },
          signal,
        }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) =>
      last.pagination.has_more ? (last.pagination.next_cursor ?? null) : null,
  });
}

// ---- the top-up channels and the accept minimum ----

/** The rules of every top-up channel, by market: a superadmin's to read and to set. */
export const topupSettingsQuery = queryOptions({
  queryKey: [...MONEY_KEY, "topup-settings"] as const,
  queryFn: async ({ signal }) => {
    const { data } = await unwrap(billingApi.GET("/v2/admin/topup-settings", { signal }));
    return data.settings;
  },
});

/**
 * What a wallet must hold for its specialist to accept a visit, a currency
 * at a time: a superadmin's to read and to set. A currency nobody has set
 * has the deployment's value, and says so (`is_default`).
 */
export const acceptMinimumsQuery = queryOptions({
  queryKey: [...MONEY_KEY, "accept-minimums"] as const,
  queryFn: async ({ signal }) => {
    const { data } = await unwrap(billingApi.GET("/v2/admin/accept-minimums", { signal }));
    return data.minimums;
  },
});

/** The markets, from hg-order: their currency, for the rules of a channel a market has none for yet. */
export const marketsQuery = queryOptions({
  queryKey: [...MONEY_KEY, "markets"] as const,
  queryFn: async ({ signal }) => {
    const { data } = await unwrap(orderApi.GET("/v2/admin/markets", { signal }));
    return data;
  },
  staleTime: 10 * 60_000,
});
